import type { DraftTaskAttachmentList, getApiReactHooks } from '@frontend/api-client';
import { convertAttachmentUrlsToAttachmentsList, handleAttachmentsForFormData } from '@frontend/api-client';
import type {
  CreateDraftTaskMutation,
  CreateTaskSource,
  TypedFormData as GenericTypedFormData,
  LocalFile,
  UpdateDraftTaskMutation,
} from '@frontend/api-types';
import type { getMutationHandler } from '@frontend/duck-tape';
import { EArray, match, serializeJson } from '@frontend/duck-tape';
import { useEffect, usePrevious } from '@frontend/react';
import { useCreateDraftTask } from '../context/CreateDraftTaskContext';

type UseDescriptionStepProps = {
  TypedFormData: typeof GenericTypedFormData;
  description?: string;
  dismissSuggestion?: (suggestionId: string) => void;
  getDescriptionForSubmit?: (description: string) => string;
  handleMutation: ReturnType<typeof getMutationHandler>;
  incrementParentStep: () => void;
  onCreateDraftSuccess?: (draftId: string) => void;
  platformSource: CreateTaskSource;
  setupSocket?: (draftTaskId: string) => void;
  useCreateDraftTaskMutation: ReturnType<typeof getApiReactHooks>['useCreateDraftTaskMutation'];
  useEditDraftTaskAfterFailureMutation: ReturnType<typeof getApiReactHooks>['useEditDraftTaskAfterFailureMutation'];
  useUpdateDraftTaskMutation: ReturnType<typeof getApiReactHooks>['useUpdateDraftTaskMutation'];
};

export const useDescriptionStep = ({
  description,
  dismissSuggestion,
  getDescriptionForSubmit,
  handleMutation,
  incrementParentStep,
  onCreateDraftSuccess,
  platformSource,
  setupSocket,
  TypedFormData,
  useCreateDraftTaskMutation,
  useEditDraftTaskAfterFailureMutation,
  useUpdateDraftTaskMutation,
}: UseDescriptionStepProps) => {
  const { setState, state } = useCreateDraftTask();

  useEffect(() => {
    if (description) setState({ description });
  }, []);

  const resolveDescription = () => {
    const base = state.description ?? '';
    return getDescriptionForSubmit ? getDescriptionForSubmit(base) : base;
  };

  const [createDraftTaskV2, { isLoading: isCreateDraftTaskV2Loading }] = useCreateDraftTaskMutation();
  const [editDraftTaskV2, { isLoading: isEditDraftTaskV2Loading }] = useUpdateDraftTaskMutation();
  const [editDraftTaskAfterFailureV2, { isLoading: isEditDraftTaskAfterFailureV2Loading }] =
    useEditDraftTaskAfterFailureMutation();

  const didAnyFieldChange =
    EArray.some(state.attachments, (a) => a.__typename === 'LocalFile') ||
    (state.draftTaskEntity?.description && state.description !== state.draftTaskEntity?.description);

  const isAnyLoading = isCreateDraftTaskV2Loading || isEditDraftTaskAfterFailureV2Loading || isEditDraftTaskV2Loading;

  const prevIsScopeCheckLoading = usePrevious(state.isScopeCheckLoading);
  const isContinueDisabled = Boolean(
    !state.description || (state.scopeFailureReason && !didAnyFieldChange) || state.isScopeCheckLoading,
  );

  useEffect(() => {
    if (state.draftTaskEntity?.reviewStatus === 'prelim_review') {
      setState({ isScopeCheckLoading: true });
    } else {
      setState({ isScopeCheckLoading: false });
      // Automatically go to proceed decision step if scope checks complete
      if (prevIsScopeCheckLoading && !state.draftTaskEntity?.scopeFailureReason) {
        incrementParentStep();
      }
    }
  }, [state.draftTaskEntity?.reviewStatus]);

  const handleUpdateDraftTaskAfterScopeCheckFailure = async (draftTaskId: string) =>
    handleMutation({
      errorMessage: 'Error updating draft task',
      mutation: async () => editDraftTaskAfterFailureV2({ draftTaskId }).unwrap(),
      onSuccess: async () => handleUpdateDraftTask(draftTaskId),
      showSuccessToast: false,
    });

  const handleUpdateDraftTask = async (draftTaskId: string) =>
    handleMutation({
      errorMessage: 'Error updating draft task',
      mutation: async () => {
        const formData = new TypedFormData<UpdateDraftTaskMutation['input']>();

        const data = serializeJson({
          description: resolveDescription(),
          task_source: platformSource,
        });

        formData.append('data', data);

        const formDataWithAttachments = handleAttachmentsForFormData({
          attachments: state.attachments,
          formData,
          originalAttachments: state.draftTaskEntity?.attachmentUrls ?? [],
        });
        return editDraftTaskV2({ draftTaskId, formData: formDataWithAttachments as FormData }).unwrap();
      },
      onSuccess: async (draftTask) => {
        setState({
          attachments: convertAttachmentUrlsToAttachmentsList(draftTask.attachmentUrls),
          draftTaskEntity: draftTask,
          scopeFailureReason: undefined,
        });
        incrementParentStep();
        return;
      },
      showSuccessToast: false,
    });

  const handleCreateDraftTask = async ({ imFeelingDucky }: { imFeelingDucky?: boolean } = {}) =>
    handleMutation({
      errorMessage: 'Error creating draft task',
      mutation: async () => {
        const formData = new TypedFormData<CreateDraftTaskMutation['input']>();

        const data = serializeJson({
          description: resolveDescription(),
          example: state.example,
          ...(imFeelingDucky && { im_feeling_ducky: true }),
          ...(state.letterRecommendationId &&
            state.letterRecommendationTaskSuggestionIdx && {
              letter_recommendation: {
                id: state.letterRecommendationId,
                task_suggestion_idx: state.letterRecommendationTaskSuggestionIdx,
              },
            }),
          ...(state.suggestionId && { suggestion_id: state.suggestionId }),
          task_source: platformSource,
        });

        formData.append(
          'data',
          data as unknown as SerializedJson<{
            description: string;
            example?: string;
            letter_recommendation?: {
              id: string;
              task_suggestion_idx: number;
            };
            suggestion_id?: string;
            task_source: CreateTaskSource;
          }>,
        );
        formData.appendLocalFiles(
          'attachments',
          state.attachments.filter((a) => a.__typename === 'LocalFile') as LocalFile[],
        );

        return createDraftTaskV2({ formData: formData as unknown as FormData }).unwrap();
      },
      onSuccess: async (draftTask) => {
        onCreateDraftSuccess?.(draftTask.id);
        if (state.suggestionId) {
          dismissSuggestion?.(state.suggestionId);
        }
        setState({
          attachments: convertAttachmentUrlsToAttachmentsList(draftTask.attachmentUrls),
          description: state.description,
          draftTaskEntity: draftTask,
        });
        if (setupSocket) {
          setupSocket(draftTask.id);
        }
        return;
      },
      showSuccessToast: false,
    });

  const onPressContinue = async () => {
    const draftTaskId = state.draftTaskEntity?.id;
    if (draftTaskId) {
      if (didAnyFieldChange) {
        if (state.scopeFailureReason) {
          return handleUpdateDraftTaskAfterScopeCheckFailure(draftTaskId);
        }
        return handleUpdateDraftTask(draftTaskId);
      }
      if (
        state.draftTaskEntity?.reviewStatus !== 'prelim_review_failed' &&
        state.draftTaskEntity?.reviewStatus !== 'final_review_failed'
      ) {
        return incrementParentStep();
      }
    } else return handleCreateDraftTask();
  };

  const onPressImFeelingDucky = async () => {
    if (!state.draftTaskEntity?.id) {
      return handleCreateDraftTask({ imFeelingDucky: true });
    }
    return undefined;
  };

  const handleDescriptionChange = (text: string) => {
    setState({ description: text });
  };

  const handleFileChoose = (attachments: DraftTaskAttachmentList) => {
    setState({ attachments });
  };

  const handleFileRemove = (file: DraftTaskAttachmentList[number]) => {
    setState({
      attachments: state.attachments.filter((a) =>
        match([a, file])
          .with([{ __typename: 'LocalFile' }, { __typename: 'LocalFile' }], ([a, file]) => a.name !== file.name)
          .with([{ __typename: 'SavedFile' }, { __typename: 'SavedFile' }], ([a, file]) => a.key !== file.key)
          .otherwise(() => true),
      ),
    });
  };

  const hasAttachments = state.attachments.length > 0;

  return {
    handleDescriptionChange,
    handleFileChoose,
    handleFileRemove,
    hasAttachments,
    isAnyLoading,
    isContinueDisabled,
    onPressContinue,
    onPressImFeelingDucky,
  };
};
